// src/pages/TicketDesigner.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useTicketDesign from "../hooks/useTicketDesign";
import TicketPreview from "../components/ticketDesign/TicketPreview";
import SectionControls from "../components/ticketDesign/SectionControls";
import ColorInput from "../components/ticketDesign/ColorInput";
import NumberInput from "../components/ticketDesign/NumberInput";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { getTicketRegenerationStatus, sampleTicketData, saveDesign, getTicketDesignDetails } from "../services/ticketDesignApi";
import { getTicketClassById } from "../services/ticket";
import { getImportStatusStreamUrl } from "../services/import";
import "../styles/ticket.css";
import { FONT_OPTIONS, WEIGHT_OPTIONS } from "../utils/fonts";
import { getDashboardContextValue } from "../utils/dashboardContext";
import DesignerUI from "../components/ticketDesign/DesignerUI";
import { buildCertificateDesign } from "../components/ticketDesign/CertificateOnboarding";
import { useSidebarStore } from "../store/useSidebarStore";
import { useSite } from "../store/useSite";
import { AlertTriangle, X } from "lucide-react";

const PREVIEW_VENUE = "Indira Gandhi National Auditorium";

const getPreviewVenue = (eventData) => {
  const venue = eventData?.venue || eventData?.location || "";
  return String(venue).trim() || PREVIEW_VENUE;
};

const formatPreviewDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPreviewTime = (value, fallbackDate) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const parsed = raw.includes("T")
    ? new Date(raw)
    : new Date(`1970-01-01T${raw}`);

    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
  }

    if (fallbackDate) {
      const combined = new Date(`${fallbackDate}T${raw}`);
      if (!Number.isNaN(combined.getTime())) {
        return combined.toLocaleTimeString("en-US", {
          hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
  }

  return raw;
};

const formatPreviewDateRange = (startValue, endValue) => {
  const start = startValue ? new Date(startValue) : null;
  const end = endValue ? new Date(endValue) : null;

  if (!start || Number.isNaN(start.getTime())) return "";
  if (!end || Number.isNaN(end.getTime())) return formatPreviewDate(startValue);

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (sameDay) {
    return formatPreviewDate(startValue);
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear) {
    const startText = start.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
    const endText = end.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return `${startText} to ${endText}`;
  }

  return `${formatPreviewDate(startValue)} to ${formatPreviewDate(endValue)}`;
};

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const formatPreviewValidityDates = (dayPriceQuantity) => {
  const config = parseMaybeJson(dayPriceQuantity);
  if (!config) {
    return "";
  }

  const dates = (Array.isArray(config?.items) ? config.items : [])
    .map((item) => item?.date || item?.day_date || item?.access_date)
    .filter(Boolean)
    .map((value) => new Date(value))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  if (!dates.length) return "";

  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return [...new Set(dates.map((date) => formatter.format(date)))].join(", ");
};

const getRegenerationProgressFromStatus = (status) => {
  const job = status?.job || status || {};
  const processed = Number(status?.progress?.processed ?? job.processed_rows ?? job.processedRows ?? 0);
  const explicitTotal = Number(status?.progress?.total ?? job.item_count ?? job.total_rows ?? job.totalRows ?? 0);
  const pending = Number(status?.progress?.pending ?? job.pending_count ?? Math.max(0, explicitTotal - processed));
  const total = Math.max(0, explicitTotal, processed + pending);
  return {
    processed: Math.max(0, processed),
    total,
    pending: Math.max(0, pending),
  };
};

const isRegenerationStatus = (status, activeJobId = null) => {
  const jobType = String(status?.job_type || status?.jobType || status?.job?.job_type || status?.job?.jobType || "");
  const statusJobId = status?.jobId || status?.id || status?.job?.id || status?.job?.jobId;
  return ["ticket_regenerate", "print_regenerate", "badge_regenerate", "send_certificate", "certificate_regenerate", "certificate_print_regenerate"].includes(jobType) || (activeJobId && Number(statusJobId) === Number(activeJobId));
};

const TEMPLATE_CATEGORY_BY_TAG = {
  withProfilePic: "ticket",
  withoutProfilePic: "ticket",
  forPrint: "print",
  badge: "badge",
  certificate: "certificate",
  forCertificatePrint: "certificate",
};

const JOB_CATEGORY_BY_TYPE = {
  ticket_regenerate: "ticket",
  print_regenerate: "print",
  badge_regenerate: "badge",
  send_certificate: "certificate",
  certificate_regenerate: "certificate",
  certificate_print_regenerate: "certificate",
};

const EMPTY_STATUS = {
  loading: false,
  isActive: false,
  job: null,
  progress: { processed: 0, total: 0, pending: 0 },
};

const getTemplateCategory = (templateTag) => TEMPLATE_CATEGORY_BY_TAG[templateTag] || "ticket";

const getJobCategory = (status) => {
  const jobType = String(status?.job_type || status?.jobType || status?.job?.job_type || status?.job?.jobType || "");
  return JOB_CATEGORY_BY_TYPE[jobType] || status?.category || status?.job?.category || null;
};

const cleanDesignForCompare = (obj) => {
  if (!obj) return null;
  const { meta, key, canvas, ...rest } = obj;
  const normalizedCanvas = canvas
    ? {
        ...canvas,
        padding: 0,
        borderWidth: 1,
      }
    : canvas;
  return JSON.parse(JSON.stringify({ ...rest, canvas: normalizedCanvas }));
};

const isSameDesign = (a, b) =>
  JSON.stringify(cleanDesignForCompare(a)) === JSON.stringify(cleanDesignForCompare(b));

export default function TicketDesigner() {
  const { id } = useParams();
  const ticketId = id;
  const navigate = useNavigate();
  const { setSidebarOpen } = useSidebarStore();
  const { eventData } = useSite();

  const [eventId] = useState(() => getDashboardContextValue("eventId"));
  const [tag, setTag] = useState("withoutProfilePic");
  const { design, setDesign, replaceDesign, loading, initialDesign, markSavedDesign, preGeneratePrintPdf, setPreGeneratePrintPdf, initialPreGeneratePrintPdf, hasCertificate, setHasCertificate } = useTicketDesign(eventId, ticketId, 'ticket_class', tag);
  const [certificateExists, setCertificateExists] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!eventId || !ticketId) return;

    getTicketDesignDetails(eventId, ticketId, 'ticket_class', 'certificate')
      .then((res) => {
        if (!cancelled) {
          setCertificateExists(Boolean(res?.design));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCertificateExists(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, ticketId]);
  const [isSaving, setIsSaving] = useState(false);
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [saveParams, setSaveParams] = useState(null);
  const [statusByCategory, setStatusByCategory] = useState({
    ticket: { ...EMPTY_STATUS, loading: true },
    print: { ...EMPTY_STATUS, loading: true },
    badge: { ...EMPTY_STATUS, loading: true },
    certificate: { ...EMPTY_STATUS, loading: true },
  });

  const [data, setData] = useState(sampleTicketData());
  const [ticketValidityPreview, setTicketValidityPreview] = useState("");
  const [selectedSection, setSelectedSection] = useState("canvas");
  const [liveTicketId, setLiveTicketId] = useState(data.ticketId);
  const activeCategory = getTemplateCategory(tag);
  const activeCategoryStatus = statusByCategory[activeCategory] || EMPTY_STATUS;
  const isDesignerLocked = activeCategoryStatus.loading || activeCategoryStatus.isActive;
  const processingTitle =
    activeCategory === "print"
      ? "Pre-generating print PDFs"
      : activeCategory === "badge"
        ? "Regenerating badges"
        : activeCategory === "certificate"
          ? (tag === "forCertificatePrint" ? "Regenerating certificate print PDFs" : "Regenerating certificates")
          : "Regenerating tickets";

  useEffect(() => {
    setData((d) => ({ ...d, ticketId: liveTicketId }));
  }, [liveTicketId]);

  useEffect(() => {
    if (!eventId) {
      setStatusByCategory({
        ticket: EMPTY_STATUS,
        print: EMPTY_STATUS,
        badge: EMPTY_STATUS,
        certificate: EMPTY_STATUS,
      });
      return undefined;
    }

    let cancelled = false;
    const activeJobIds = {};

    const applyStatus = (status, fallbackCategory = "ticket") => {
      const category = getJobCategory(status) || fallbackCategory;
      const isActive = Boolean(status?.isActive ?? (String(status?.status || "") !== "completed"));
      if (status?.job?.id || status?.job?.jobId || status?.id || status?.jobId) {
        activeJobIds[category] = status?.job?.id || status?.job?.jobId || status?.id || status?.jobId;
      }
      setStatusByCategory((prev) => ({
        ...prev,
        [category]: {
          loading: false,
          isActive,
          job: status?.job || status || null,
          progress: getRegenerationProgressFromStatus(status),
        },
      }));
    };

    const refreshStatuses = async () => {
      try {
        const entries = await Promise.all(
          ["ticket", "print", "badge", "certificate"].map(async (category) => {
            const status = await getTicketRegenerationStatus(eventId, { ticketId, category });
            return [category, status];
          }),
        );
        if (!cancelled) {
          entries.forEach(([category, status]) => applyStatus(status, category));
        }
      } catch (error) {
        if (!cancelled) {
          setStatusByCategory((prev) => ({
            ticket: { ...(prev.ticket || EMPTY_STATUS), loading: false },
            print: { ...(prev.print || EMPTY_STATUS), loading: false },
            badge: { ...(prev.badge || EMPTY_STATUS), loading: false },
            certificate: { ...(prev.certificate || EMPTY_STATUS), loading: false },
          }));
        }
      }
    };

    refreshStatuses();
    const poll = setInterval(refreshStatuses, 3000);
    const source = new EventSource(getImportStatusStreamUrl(eventId), { withCredentials: true });
    const handleStatusEvent = (event) => {
      try {
        const status = JSON.parse(event.data);
        const category = getJobCategory(status);
        if (!status || status.heartbeat || !isRegenerationStatus(status, category ? activeJobIds[category] : null)) {
          return;
        }
        if (!cancelled) {
          applyStatus({
            ...status,
            isActive: String(status.status || "") !== "completed",
          }, category || "ticket");
        }
      } catch {}
    };

    source.addEventListener("status", handleStatusEvent);
    source.onmessage = handleStatusEvent;
    source.onerror = () => {};

    return () => {
      cancelled = true;
      clearInterval(poll);
      source.close();
    };
  }, [eventId, ticketId]);

  useEffect(() => {
    if (eventData) {
      const combinedDateText = formatPreviewDateRange(
        eventData.start_date,
        eventData.end_date || eventData.start_date,
      );
      const startTimeText =
        formatPreviewTime(eventData.start_time, eventData.start_date) ||
        formatPreviewTime(eventData.start_date);
      const previewDateText = startTimeText
        ? `${combinedDateText} | ${startTimeText}`
        : combinedDateText;

      setData((prev) => ({
        ...prev,
        eventName: eventData.event_name || prev.eventName,
        venue: getPreviewVenue(eventData),
        date: previewDateText || prev.date,
        ticketValidDates: ticketValidityPreview,
        start_time: startTimeText || prev.start_time,
      }));
    }
  }, [eventData, ticketValidityPreview]);

  useEffect(() => {
    let cancelled = false;
    if (!ticketId) {
      setTicketValidityPreview("");
      return undefined;
    }

    getTicketClassById(ticketId)
      .then((res) => {
        if (cancelled) return;
        const ticketClass = res?.data?.data || res?.data?.ticket || res?.data;
        setTicketValidityPreview(formatPreviewValidityDates(ticketClass?.day_price_quantity));
      })
      .catch(() => {
        if (!cancelled) setTicketValidityPreview("");
      });

    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  const onDragEnd = (result) => {
    if (!design || !result.destination) return;
    const newOrder = Array.from(design.sectionsOrder);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setDesign({ sectionsOrder: newOrder });
  };

  const updateCanvas = useCallback((patch) => {
    setDesign((prev) => ({ ...prev, canvas: { ...prev.canvas, ...patch } }));
  }, [setDesign]);

  const updateHeader = useCallback((patch) => {
    setDesign((prev) => ({ ...prev, header: { ...prev.header, ...patch } }));
  }, [setDesign]);

  const toggleSection = (id, enabled) => {
    setDesign((prev) => ({
      ...prev,
      sections: { ...prev.sections, [id]: { ...prev.sections[id], enabled } },
    }));
  };

  const updateSection = useCallback((id, nextOrPatch) => {
    setDesign((prev) => {
      const prevSec = prev.sections[id] || {};
      const next =
        typeof nextOrPatch === "function"
          ? nextOrPatch(prevSec)
          : { ...prevSec, ...nextOrPatch };
      return {
        ...prev,
        sections: { ...prev.sections, [id]: next },
      };
    });
  }, [setDesign]);

  const executeSave = async (regenerateExisting) => {
    const { designOverride, shouldNavigate, successMessage } = saveParams || {};
    const designToSave = designOverride || design;
    setIsSaving(true);
    setRegenerateModalOpen(false);
    try {
      const result = await saveDesign(eventId, ticketId, designToSave, 'ticket_class', tag, {
        regenerateExisting,
        preGeneratePrintPdf,
      });
      const regenerationJob = result?.data?.regenerationJob || result?.regenerationJob || result?.data?.certificatePreGeneration || result?.certificatePreGeneration;
      markSavedDesign(designToSave, preGeneratePrintPdf);

      if (tag === "certificate") {
        setCertificateExists(true);
        setHasCertificate(true);
        const isFirstTime = !initialDesign;
        const certPrintDesign = {
          ...designToSave,
          key: "forCertificatePrint",
          meta: { ...(designToSave?.meta || {}), template: "forCertificatePrint" },
          canvas: {
            ...(designToSave?.canvas || {}),
            printBackgroundImage: designToSave?.canvas?.printBackgroundImage !== false,
          },
        };
        try {
          localStorage.setItem(
            `qe_ticket_design:${eventId}:${ticketId}:forCertificatePrint`,
            JSON.stringify(certPrintDesign)
          );
        } catch {}
        if (isFirstTime) {
          try {
            await saveDesign(eventId, ticketId, certPrintDesign, 'ticket_class', 'forCertificatePrint', {
              regenerateExisting: false,
              preGeneratePrintPdf,
            });
          } catch (e) {}
        }
      }

      const copiedToCertPrint = Boolean(result?.data?.copiedToCertificatePrint || (tag === "certificate" && !initialDesign));
      const displayMsg = copiedToCertPrint
        ? `${successMessage} Copied to For Certificate Print template.`
        : successMessage;

      toast.success(regenerationJob ? `${displayMsg} Regeneration job started.` : displayMsg, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      if (shouldNavigate) {
        setTimeout(() => {
          navigate(-1); // Go back to previous page
        }, 1500);
      }
      return true;
    } catch (e) {
      console.error("Error saving ticket design:", e);
      toast.error(e?.message || "Failed to save ticket design", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return false;
    } finally {
      setIsSaving(false);
      setSaveParams(null);
    }
  };

  const handleSave = async (options = {}) => {
    if (isDesignerLocked) {
      toast.info(activeCategoryStatus.loading
        ? "Checking ticket processing status. Please wait a moment."
        : "This template category has a running job. You can switch to another category and edit there.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return false;
    }

    const {
      designOverride,
      shouldNavigate = true,
      successMessage = "Ticket design saved successfully!",
      askRegenerateExisting = shouldNavigate,
      forceSave = false,
    } = options;
    const designToSave = designOverride || design;
    const designChanged = !isSameDesign(designToSave, initialDesign);
    const preGenerateChanged = (tag === "forPrint" || tag === "certificate" || tag === "forCertificatePrint") && Boolean(preGeneratePrintPdf) !== Boolean(initialPreGeneratePrintPdf);
    const hasChanges = designChanged || preGenerateChanged;

    if (!eventId || !ticketId || !designToSave) {
      toast.error("Missing eventId, ticketId, or design", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    if (!hasChanges && !forceSave) {
      toast.info("No changes to save", {
        position: "top-right",
        autoClose: 1800,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      if (shouldNavigate) {
        setTimeout(() => {
          navigate(-1);
        }, 700);
      }
      return true;
    }
    
    if (askRegenerateExisting) {
      setSaveParams({ designOverride, shouldNavigate, successMessage });
      setRegenerateModalOpen(true);
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveDesign(eventId, ticketId, designToSave, 'ticket_class', tag, {
        regenerateExisting: false,
        preGeneratePrintPdf,
      });
      const regenerationJob = result?.data?.regenerationJob || result?.regenerationJob || result?.data?.certificatePreGeneration || result?.certificatePreGeneration;
      markSavedDesign(designToSave, preGeneratePrintPdf);

      if (tag === "certificate") {
        setCertificateExists(true);
        setHasCertificate(true);
        const isFirstTime = !initialDesign;
        const certPrintDesign = {
          ...designToSave,
          key: "forCertificatePrint",
          meta: { ...(designToSave?.meta || {}), template: "forCertificatePrint" },
          canvas: {
            ...(designToSave?.canvas || {}),
            printBackgroundImage: designToSave?.canvas?.printBackgroundImage !== false,
          },
        };
        try {
          localStorage.setItem(
            `qe_ticket_design:${eventId}:${ticketId}:forCertificatePrint`,
            JSON.stringify(certPrintDesign)
          );
        } catch {}
        if (isFirstTime) {
          try {
            await saveDesign(eventId, ticketId, certPrintDesign, 'ticket_class', 'forCertificatePrint', {
              regenerateExisting: false,
              preGeneratePrintPdf,
            });
          } catch (e) {}
        }
      }

      const copiedToCertPrint = Boolean(result?.data?.copiedToCertificatePrint || (tag === "certificate" && !initialDesign));
      const displayMsg = copiedToCertPrint
        ? `${successMessage} Copied to For Certificate Print template.`
        : successMessage;

      toast.success(regenerationJob ? `${displayMsg} Regeneration job started.` : displayMsg, {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      if (shouldNavigate) {
        setTimeout(() => {
          navigate(-1); // Go back to previous page
        }, 1500);
      }
      return true;
    } catch (e) {
      console.error("Error saving ticket design:", e);
      toast.error(e?.message || "Failed to save ticket design", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackgroundImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image size should be less than 10MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        updateCanvas({ backgroundImage: event.target.result });
      };
      reader.readAsDataURL(file);
    }
  }, [updateCanvas]);

  const handleRemoveBackgroundImage = useCallback(() => {
    updateCanvas({ backgroundImage: "", backgroundImageOpacity: 0.5 });
  }, [updateCanvas]);

  const controlsPanel = useMemo(() => {
    if (!design) return null;

    if (selectedSection === "background") {
      return (
        <div className="controls-group">
          <label className="ctrl">
            <span>Upload Background Image</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleBackgroundImageChange}
              style={{ marginTop: "4px", width: "100%" }}
            />
          </label>
          {design.canvas.backgroundImage ? (
            <>
              <div style={{ marginTop: "12px", marginBottom: "12px" }}>
                <label className="ctrl" style={{ marginBottom: "8px" }}>
                  <span>Preview</span>
                </label>
                <img
                  src={design.canvas.backgroundImage}
                  alt="Background preview"
                  style={{
                    width: "100%",
                    maxHeight: "150px",
                    objectFit: "contain",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                    backgroundColor: "#f5f5f5",
                  }}
                />
              </div>
              <label className="ctrl">
                <span>Opacity (%)</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round((design.canvas.backgroundImageOpacity ?? 0.5) * 100)}
                  onChange={(e) => {
                    let inputValue = e.target.value;
                    
                    // Remove leading zeros (but keep single 0)
                    if (inputValue.length > 1 && inputValue.startsWith('0')) {
                      // Remove leading zeros, but if result is empty, keep it as "0"
                      inputValue = inputValue.replace(/^0+/, '') || '0';
                      // Update the input field value
                      e.target.value = inputValue;
                    }
                    
                    const numValue = inputValue === "" ? 0 : parseInt(inputValue, 10);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      const opacity = numValue / 100;
                      updateCanvas({ backgroundImageOpacity: opacity });
                    }
                  }}
                />
              </label>
              <button
                className="btn"
                onClick={handleRemoveBackgroundImage}
                style={{ width: "100%", marginTop: "12px" }}
              >
                Remove Background Image
              </button>
            </>
          ) : (
            <div style={{ marginTop: "12px", padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "14px", border: "1px dashed #d1d5db", borderRadius: "8px", background: "#f9fafb" }}>
              No background image uploaded.
            </div>
          )}
        </div>
      );
    }

    if (selectedSection === "canvas") {
      return (
        <div className="controls-group">
          <ColorInput
            label="Background"
            value={design.canvas.background}
            onChange={(v) => updateCanvas({ background: v })}
          />
          <ColorInput
            label="Border color"
            value={design.canvas.borderColor}
            onChange={(v) => updateCanvas({ borderColor: v })}
          />
          <NumberInput
            label="Border width"
            value={design.canvas.borderWidth}
            min={1}
            max={8}
            onChange={(v) => updateCanvas({ borderWidth: v })}
          />
          <NumberInput
            label="Border radius"
            value={design.canvas.borderRadius}
            min={0}
            max={40}
            onChange={(v) => updateCanvas({ borderRadius: v })}
          />
          <NumberInput
            label="Padding"
            value={design.canvas.padding}
            min={0}
            max={40}
            onChange={(v) => updateCanvas({ padding: v })}
          />
          <NumberInput
            label="Width"
            value={design.canvas.width}
            min={280}
            max={560}
            onChange={(v) => updateCanvas({ width: v })}
          />
          <NumberInput
            label="Height"
            value={design.canvas.height}
            min={420}
            max={800}
            onChange={(v) => updateCanvas({ height: v })}
          />
        </div>
      );
    }

    if (selectedSection === "header") {
      return (
        <div className="controls-group">
          <label className="ctrl row-start">
            <input
              type="checkbox"
              checked={design.header.enabled}
              onChange={(e) => updateHeader({ enabled: e.target.checked })}
            />
            <span>Show header</span>
          </label>
          <ColorInput
            label="Header background"
            value={design.header.bg}
            onChange={(v) => updateHeader({ bg: v })}
          />
          <ColorInput
            label="Header text color"
            value={design.header.color}
            onChange={(v) => updateHeader({ color: v })}
          />
          <NumberInput
            label="Header height"
            value={design.header.height}
            min={36}
            max={120}
            onChange={(v) => updateHeader({ height: v })}
          />
          <label className="ctrl">
            <span>Text</span>
            <input
              className="ctrl-text"
              value={design.header.text}
              onChange={(e) => updateHeader({ text: e.target.value })}
            />
          </label>
          <label className="ctrl">
            <span>Font</span>
            <select
              value={design.header.fontFamily}
              onChange={(e) => updateHeader({ fontFamily: e.target.value })}
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label className="ctrl">
            <span>Weight</span>
            <select
              value={design.header.fontWeight}
              onChange={(e) =>
                updateHeader({ fontWeight: Number(e.target.value) })
              }
            >
              {WEIGHT_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>
          <NumberInput
            label="Font size"
            value={design.header.fontSize}
            min={10}
            max={28}
            onChange={(v) => updateHeader({ fontSize: v })}
          />
          <label className="ctrl">
            <span>Align</span>
            <select
              value={design.header.align}
              onChange={(e) => updateHeader({ align: e.target.value })}
            >
              <option value="left">left</option>
              <option value="center">center</option>
              <option value="right">right</option>
            </select>
          </label>
        </div>
      );
    }

    const def = design.sections[selectedSection];
    if (!def) return null;

    return (
      <SectionControls
        id={selectedSection}
        def={def}
        onChange={(next) => updateSection(selectedSection, next)}
      />
    );
  }, [design, selectedSection, updateCanvas, updateHeader, updateSection, handleBackgroundImageChange, handleRemoveBackgroundImage]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600 font-medium">Loading ticket design...</p>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <DesignerUI
        title="Ticket Designer"
        onCertificatePickImage={(backgroundImage) =>
          replaceDesign(
            tag === "forCertificatePrint"
              ? buildCertificateDesign({
                  backgroundImage,
                  template: "forCertificatePrint",
                  key: "forCertificatePrint",
                })
              : buildCertificateDesign({ backgroundImage })
          )
        }
        onCertificatePickEmpty={() =>
          replaceDesign(
            tag === "forCertificatePrint"
              ? buildCertificateDesign({
                  template: "forCertificatePrint",
                  key: "forCertificatePrint",
                })
              : buildCertificateDesign()
          )
        }
        design={design}
        initialDesign={initialDesign}
        setDesign={setDesign}
        replaceDesign={replaceDesign}
        data={data}
        setData={setData}
        isSaving={isSaving}
        onSave={handleSave}
        onBack={() => navigate(-1)}
        tag={tag}
        setTag={setTag}
        preGeneratePrintPdf={preGeneratePrintPdf}
        setPreGeneratePrintPdf={setPreGeneratePrintPdf}
        initialPreGeneratePrintPdf={initialPreGeneratePrintPdf}
        readOnly={isDesignerLocked}
        readOnlyMessage={
          activeCategoryStatus.loading
            ? "Checking this template category. Editing will open automatically when processing is clear."
            : "This template category is processing. Switch to another category to keep editing."
        }
        readOnlyLoading={activeCategoryStatus.loading}
        processingProgress={activeCategoryStatus.progress}
        processingTitle={processingTitle}
        lockedCategories={statusByCategory}
        eventId={eventId}
        ticketClassId={ticketId}
        hasCertificate={Boolean(certificateExists || hasCertificate)}
      />
      {regenerateModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform animate-in zoom-in-95 duration-200 border border-gray-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
                    <AlertTriangle size={20} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {tag === "certificate" || tag === "forCertificatePrint" ? "Regenerate Certificates?" : "Regenerate Tickets?"}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setRegenerateModalOpen(false);
                    setSaveParams(null);
                  }}
                  className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed mb-1">
                {tag === "certificate" || tag === "forCertificatePrint"
                  ? "Do you want to regenerate certificates for all existing registrations with this new template?"
                  : "Do you want to regenerate tickets for all existing registrations with this new template?"}
              </p>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row gap-2 justify-end border-t border-gray-100">
              <button
                type="button"
                onClick={() => executeSave(true)}
                className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 transition"
              >
                Regenerate all registrations
              </button>
              <button
                type="button"
                onClick={() => executeSave(false)}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 shadow-md shadow-indigo-100 transition"
              >
                New registrations only
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
